import { auth } from '@/firebase'
import { setTag } from '@sentry/vue'
import { RouteRecordRaw, createRouter, createWebHistory } from 'vue-router'

const routes: Array<RouteRecordRaw> = [
  // =========================================
  // Nitte
  // =========================================

  /**
   * Nitte - 認証ページ
   */
  {
    path: '/signup',
    name: 'Signup',
    component: () => import('../pages/Signup/Signup.vue')
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('../pages/Login/Login.vue')
  },
  {
    path: '/loginToken',
    name: 'OauthCallback',
    component: () => import('../pages/LoginToken.vue')
  },
  {
    path: '/user_deleted',
    name: 'UserDeleted',
    component: () => import('../pages/UserDeleted.vue')
  },

  /**
   * Nitte - 認証済みユーザーが使用するページ
   */
  {
    path: '/',
    name: 'Pages',
    meta: {
      showHeader: true,
      requiredAuth: true
    },
    component: () => import('../pages/Home/Pages/Pages.vue')
  },
  {
    path: '/new',
    name: 'New',
    meta: {
      showHeader: true,
      requiredAuth: true
    },
    component: () => import('../pages/Home/New/New.vue')
  },
  {
    path: '/edit/:uid/:pageId',
    name: 'Edit',
    meta: {
      showHeader: true,
      requiredAuth: true
    },
    component: () => import('../pages/Home/Edit/Edit.vue')
  },
  {
    path: '/bookings/:uid/:pageId',
    name: 'Bookings',
    meta: {
      showHeader: true,
      requiredAuth: true
    },
    component: () => import('../pages/Home/Bookings/Bookings.vue')
  },
  {
    path: '/bookings/:uid/:pageId/:bookingId',
    name: 'BookingDetail',
    meta: {
      showHeader: true,
      requiredAuth: true
    },
    component: () =>
      import('../pages/Home/Bookings/BookingDetail/BookingDetail.vue')
  },
  /**
   * Nitte - 設定ページ（SettingBase配下）
   */
  {
    path: '/settings/scheduling',
    name: 'SchedulingSetting',
    // アカウント設定に統合された
    redirect: '/settings/account'
  },
  {
    path: '/settings',
    component: () => import('../pages/Home/Settings/SettingBase.vue'),
    meta: {
      showHeader: true,
      requiredAuth: true
    },
    children: [
      {
        path: 'account',
        name: 'AccountSetting',
        component: () =>
          import('../pages/Home/Settings/AccountSetting/AccountSetting.vue')
      },
      {
        path: 'payment',
        name: 'PaymentSetting',
        component: () => import('../pages/Home/Settings/PaymentSetting.vue')
      },
      {
        path: 'external_tools',
        name: 'ExternalToolSetting',
        component: () =>
          import(
            '../pages/Home/Settings/ExternalToolSetting/ExternalToolSetting.vue'
          )
      },
      {
        path: 'add_functions',
        name: 'AddFunctionsSetting',
        component: () =>
          import(
            '../pages/Home/Settings/AddFunctionsSetting/AddFunctionsSetting.vue'
          )
      },
      {
        path: 'team',
        name: 'TeamSetting',
        component: () => import('../pages/Home/Settings/Team/Show/Show.vue')
      },
      {
        path: 'team/create',
        name: 'TeamCreate',
        component: () => import('../pages/Home/Settings/Team/Create/Create.vue')
      },
      {
        path: 'forms',
        name: 'IndexForm',
        component: () =>
          import('../pages/Home/Settings/FormSetting/Index/Index.vue')
      },
      {
        path: 'forms/new',
        name: 'NewForm',
        component: () =>
          import('../pages/Home/Settings/FormSetting/NewOrEdit/NewOrEdit.vue')
      },
      {
        path: 'forms/edit/:teamId/:formId',
        name: 'EditForm',
        component: () =>
          import('../pages/Home/Settings/FormSetting/NewOrEdit/NewOrEdit.vue')
      },
      {
        path: 'booking_mail_templates',
        name: 'IndexBookingMailTemplate',
        component: () =>
          import(
            '../pages/Home/Settings/BookingMailTemplateSetting/Index/Index.vue'
          )
      },
      {
        path: 'booking_mail_templates/new',
        name: 'NewBookingMailTemplate',
        component: () =>
          import(
            '../pages/Home/Settings/BookingMailTemplateSetting/NewOrEdit/NewOrEdit.vue'
          )
      },
      {
        path: 'booking_mail_templates/edit/:teamId/:templateId',
        name: 'EditBookingMailTemplate',
        component: () =>
          import(
            '../pages/Home/Settings/BookingMailTemplateSetting/NewOrEdit/NewOrEdit.vue'
          )
      }
    ]
  },

  /**
   * Nitte - 調整ページ
   */
  {
    path: '/:userId/confirmed',
    name: 'BookingConfirmed',
    component: () => import('../pages/Booking/UrlBooking/Confirmed.vue'),
    meta: {
      noClack: true
    }
  },
  {
    path: '/:userId/rescheduled',
    name: 'BookingRescheduled',
    component: () => import('../pages/Booking/UrlBooking/Rescheduled.vue'),
    meta: {
      noClack: true
    }
  },
  {
    path: '/:ownerUid/:pageId',
    name: 'Booking',
    meta: {
      noClack: true
    },
    component: () => import('../pages/Booking/UrlBooking/UrlBooking.vue')
  },
  {
    path: '/:ownerUid/cancel/:bookingId',
    name: 'CancelBooking',
    meta: {
      noClack: true
    },
    component: () =>
      import('../pages/Booking/CancelBooking/CancelUrlBooking.vue')
  },
  {
    path: '/:userId/cancel/:bookingId/completed',
    name: 'CancelCompleted',
    meta: {
      noClack: true
    },
    component: () =>
      import('../pages/Booking/CancelBooking/CancelCompleted.vue')
  },

  // =========================================
  // Nitte 人材紹介 (/mediatories)
  // =========================================

  /**
   * Nitte 人材紹介 - 認証ページ
   */
  {
    path: '/signup_mediatory',
    name: 'SignupMediatory',
    component: () => import('../pages/Signup/SignupMediatory.vue')
  },
  {
    path: '/login_mediatory',
    name: 'LoginMediatory',
    component: () => import('../pages/Login/LoginMediatory.vue')
  },

  /**
   * Nitte 人材紹介 - Join（チーム未所属）
   */
  {
    path: '/mediatories/join',
    name: 'MediatoryJoin',
    meta: {
      requiredAuth: true
    },
    component: () =>
      import('../pages/Mediatory/MediatoryJoin/MediatoryJoin.vue')
  },

  /**
   * Nitte 人材紹介 - 認証済みユーザーが使用するページ（MediatoryHomeBase配下）
   */
  {
    path: '/mediatories',
    component: () =>
      import('../pages/Mediatory/common/components/MediatoryHomeBase.vue'),
    meta: {
      requiredAuth: true
    },
    children: [
      {
        path: '',
        name: 'MediatoryHome',
        component: () =>
          import('../pages/Mediatory/MediatoryHome/MediatoryHome.vue'),
        children: [
          {
            path: 'detail/:teamId/:bookingId',
            component: () =>
              import(
                '../pages/Mediatory/MediatoryHome/parts/MediatoryDetailModal/MediatoryDetailModal.vue'
              ),
            props: true
          }
        ]
      },
      {
        path: 'new',
        name: 'MediatoryNew',
        component: () =>
          import('../pages/Mediatory/MediatoryNew/MediatoryNew.vue')
      },
      {
        path: 'edit/:teamId/:bookingId',
        name: 'MediatoryEdit',
        component: () => import('../pages/Mediatory/MediatoryEdit.vue')
      },
      {
        path: 'settings',
        component: () =>
          import(
            '../pages/Mediatory/MediatorySettings/_common/MediatorySettingBase.vue'
          ),
        children: [
          // ラベル設定はチーム設定へ統合した
          {
            path: 'participant_type_label',
            name: 'MediatoryParticipantTypeLabelSetting',
            redirect: { name: 'MediatoryTeamSetting' }
          },
          {
            path: 'clients',
            name: 'MediatoryClientsSetting',
            component: () =>
              import('../pages/Mediatory/MediatorySettings/ClientsSetting.vue')
          },
          {
            path: 'candidates',
            name: 'MediatoryCandidatesSetting',
            component: () =>
              import(
                '../pages/Mediatory/MediatorySettings/CandidatesSetting.vue'
              )
          },
          {
            path: 'booking_page',
            name: 'MediatoryBookingPageSetting',
            meta: { requiresTeamAdmin: true },
            component: () =>
              import(
                '../pages/Mediatory/MediatorySettings/BookingPageSetting/BookingPageSetting.vue'
              )
          },
          // 「機能を追加」は解体し、各トグルを関連する設定ページへ移設した
          {
            path: 'functions',
            name: 'MediatoryAddFunctionsSetting',
            redirect: { name: 'MediatoryBookingPageSetting' }
          },
          {
            path: 'mail',
            name: 'MediatoryMailSetting',
            meta: { requiresTeamAdmin: true },
            component: () =>
              import(
                '../pages/Mediatory/MediatorySettings/MailSetting/MailSetting.vue'
              )
          },
          {
            path: 'calendar',
            name: 'MediatoryCalendarSetting',
            meta: { requiresTeamAdmin: true },
            component: () =>
              import(
                '../pages/Mediatory/MediatorySettings/CalendarSetting/CalendarSetting.vue'
              )
          },
          {
            path: 'notification',
            name: 'MediatoryNotificationSetting',
            meta: { requiresTeamAdmin: true },
            component: () =>
              import(
                '../pages/Mediatory/MediatorySettings/NotificationSetting/NotificationSetting.vue'
              )
          },
          {
            path: 'porters',
            name: 'MediatoryPortersSetting',
            meta: { requiresTeamAdmin: true },
            component: () =>
              import(
                '../pages/Mediatory/MediatorySettings/PortersSetting/PortersSetting.vue'
              )
          },
          {
            path: 'brand',
            name: 'MediatoryBrandSetting',
            redirect: { name: 'MediatoryTeamSetting' }
          },
          {
            path: 'remind',
            name: 'MediatoryRemindSetting',
            meta: { requiresTeamAdmin: true },
            component: () =>
              import(
                '../pages/Mediatory/MediatorySettings/RemindSetting/RemindSetting.vue'
              )
          },
          {
            path: 'developer',
            name: 'MediatoryDeveloperSetting',
            meta: { requiresTeamAdmin: true },
            component: () =>
              import(
                '../pages/Mediatory/MediatorySettings/DeveloperSetting/DeveloperSetting.vue'
              )
          },
          // クライアントフォーム設定は調整ページ設定へ統合した
          {
            path: 'form',
            name: 'MediatoryFormSetting',
            redirect: { name: 'MediatoryBookingPageSetting' }
          },
          {
            path: 'account',
            name: 'MediatoryAccountSetting',
            meta: {
              showHeader: false
            },
            component: () =>
              import(
                '../pages/Mediatory/MediatorySettings/AccountSetting/AccountSetting.vue'
              )
          },
          {
            path: 'team',
            name: 'MediatoryTeamSetting',
            meta: {
              showHeader: false,
              requiresTeamAdmin: true
            },
            component: () =>
              import(
                '../pages/Mediatory/MediatorySettings/TeamSetting/TeamSetting.vue'
              )
          },
          {
            path: 'team/create',
            name: 'MediatoryTeamCreate',
            meta: {
              showHeader: false
            },
            component: () =>
              import(
                '../pages/Mediatory/MediatorySettings/TeamSetting/TeamCreate.vue'
              )
          }
        ]
      }
    ]
  },

  /**
   * Nitte 人材紹介 - 調整ページ
   */
  {
    path: '/mediatories/:teamId/:bookingId/:participantId',
    name: 'MediatoryBooking',
    meta: {
      noClack: true
    },
    component: () =>
      import('../pages/Mediatory/MediatoryBooking/MediatoryBooking.vue')
  },
  {
    path: '/mediatories/:teamId/:bookingId/:participantId/proposed',
    name: 'MediatoryProposed',
    meta: {
      noClack: true
    },
    component: () =>
      import(
        '../pages/Mediatory/MediatoryBooking/Propose/ProposedContainer.vue'
      )
  },
  {
    path: '/mediatories/:teamId/:bookingId/:participantId/selected',
    name: 'MediatorySelected',
    meta: {
      noClack: true
    },
    component: () =>
      import('../pages/Mediatory/MediatoryBooking/Select/SelectedContainer.vue')
  },
  {
    path: '/mediatories/:teamId/:bookingId/:participantId/confirmed',
    name: 'MediatoryConfirmed',
    meta: {
      noClack: true
    },
    component: () =>
      import(
        '../pages/Mediatory/MediatoryBooking/Confirmed/ConfirmedContainer.vue'
      )
  },

  /**
   * Nitte 人材紹介 - 企業/求職者向けページ
   */
  {
    path: '/mediatories/:teamId/participants/:participantId/auth',
    name: 'MediatoryParticipantAuth',
    component: () =>
      import(
        '../pages/Mediatory/MediatoryParticipants/ParticipantAuth/ParticipantAuth.vue'
      )
  },
  {
    path: '/mediatories/:teamId/participants/:participantId/bookings',
    name: 'MediatoryParticipantBookings',
    component: () =>
      import(
        '../pages/Mediatory/MediatoryParticipants/ParticipantBookings/ParticipantBookings.vue'
      )
  },

  // =========================================
  // その他
  // =========================================

  /**
   * 開発用
   */
  {
    path: '/playground',
    name: 'Playground',
    component: () => import('../pages/Playground.vue'),
    props: (route) => ({ query: route.query.q }),
    meta: {}
  },
  {
    path: '/prototype',
    name: 'Prototype',
    component: () => import('../pages/Prototype.vue'),
    meta: {}
  },
  {
    path: '/playwright-login',
    name: 'PlaywrightLogin',
    component: () => import('../pages/PlaywrightLogin/PlaywrightLogin.vue'),
    meta: {}
  },

  /**
   * 404
   */
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('../pages/NotFound.vue')
  }
]

// メンテ用のルート
const inMaintenanceRoute = [
  {
    path: '/:pathMatch(.*)*',
    name: 'InMaintenance',
    component: () => import('../pages/InMaintenance.vue')
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes: import.meta.env.VITE_APP_MAINTENANCE_MODE
    ? inMaintenanceRoute
    : routes
})

router.beforeEach(async (to, from, next) => {
  // 開発環境専用ページのチェック
  if (to.name === 'PlaywrightLogin' && import.meta.env.MODE !== 'development') {
    console.warn('PlaywrightLoginは開発環境でのみ使用できます')
    next({ name: 'NotFound' })
    return
  }

  // セッションが存在しなければログイン画面へ遷移
  const isRequiredAuthPage = to.matched.some(
    (record) => record.meta.requiredAuth
  )
  if (isRequiredAuthPage) {
    const currentUser = auth.currentUser
    // セッションが存在するか判定
    if (currentUser) {
      // セッションがーが存在する
      next()
    } else {
      // セッションが存在しない
      next({ name: 'Login', query: { redirectTo: to.fullPath } })
    }
  } else {
    // セッション不要ページ
    next()
  }
})

// 画面名を Sentry のタグに載せる（体験計測のためどの画面で起きたかを絞り込めるようにする）。
// main.ts ではなく router 側に置くことで、アプリの起動経路に関わらず必ず登録される。
router.afterEach((to) => {
  setTag('route', to.name ? String(to.name) : 'unknown')
})

// 動的インポートのエラーハンドリング
// チャンクロード失敗時に自動リロードする（デプロイ後の古いチャンク参照を解決）
router.onError((error) => {
  const isChunkLoadError =
    error.message?.includes('Failed to fetch dynamically imported module') ||
    error.message?.includes('error loading dynamically imported module')

  if (isChunkLoadError) {
    // sessionStorageを使用して無限ループを防ぐ
    const hasReloaded = sessionStorage.getItem('chunk-load-retry')

    if (!hasReloaded) {
      sessionStorage.setItem('chunk-load-retry', 'true')
      // ページをリロードして最新のチャンクを取得
      window.location.reload()
    } else {
      // リロード後も失敗した場合はフラグをクリアしてエラーを表示
      sessionStorage.removeItem('chunk-load-retry')
      console.error('チャンクのロードに失敗しました:', error)
      // Sentryにエラーを送信
      if (import.meta.env.PROD) {
        import('@sentry/vue').then(({ captureException }) => {
          captureException(error)
        })
      }
    }
  }
})

export default router
